import styles from "./Layout.module.scss";
import { IconButton } from "../button/iconButton/IconButton";
import { IconType } from "../../icons/Icon";
import { classNames } from "primereact/utils";
import { useAppContext } from "../../../AppContext";
import { RenderWhen } from "../../infrastructure/components/RenderWhen";
import { useEffect, useMemo, useState } from "react";
import { SharedSidebar } from "./components/SharedSidebar";
import { BackButton } from "./components/BackButton";
import { useApiHelperAppContext } from "../../infrastructure/appContexts/ApiHelperAppContext";
import { Loading } from "../loading/Loading";
import { SelectStudioForm } from "./components/SelectStudioForm";
import { StudioAccessConfirmDialog } from "../studioAccess/StudioAccessConfirmDialog";
import { matchPath, useLocation } from "react-router-dom";
import { OverflowingParagraph } from "../text/OverflowingParagraph";
import { restoreScrollPositionOnBlur } from "./utilities/restoreScrollPositionOnBlur";
import {
  applyThemeColors,
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  generateHexFromString,
  getStudioAccent,
} from "../../infrastructure/utilities/colorUtility";
import { Title } from "react-head";
import { AppDialog } from "../AppDialog/AppDialog";
import { AddPhoneNumberForm } from "./components/AddPhoneNumberForm";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { routePaths } from "../../infrastructure/routes/routePaths";
import { usePendingReservationHandler } from "../../infrastructure/hooks/usePendingReservationHandler";
import { useServiceWorkerRefresh } from "../../infrastructure/hooks/useServiceWorkerRefresh";
import { useSupportUnreadCount } from "../../infrastructure/hooks/useSupportUnreadCount";
import {
  useOnboarding,
  stepMatchesPath,
} from "../../../components/onboarding/OnboardingProvider";
import { NotificationPermissionDialog } from "../../../components/notifications/NotificationPermissionDialog";
import { AnnouncementDialog } from "../../../components/announcements/AnnouncementDialog";
import { useAnnouncements } from "../../../components/announcements/hooks/useAnnouncements";
import { NotificationDialogAudience } from "../../enums";

interface ILayoutProps {
  className?: string;
  children: React.ReactNode;
}

// Routes that should render without the layout
const routesWithoutLayout = [
  routePaths.portalAppointment,
  routePaths.portalGroupClassWizard,
  // Add more routes here that should be layout-free
];

export const Layout = ({ className, children }: ILayoutProps) => {
  const [showSelectStudioDialog, setShowSelectStudioDialog] = useState(false);
  const location = useLocation();
  const onboarding = useOnboarding();

  const currentPageStep = useMemo(() => {
    if (!onboarding?.effectiveSteps) return null;
    // Prefer the override set by tab-aware components (e.g. EventWizard)
    if (onboarding.helpStepOverride) {
      return (
        onboarding.effectiveSteps.find(
          (s) => s.id === onboarding.helpStepOverride,
        ) ?? null
      );
    }
    return (
      onboarding.effectiveSteps.find(
        (s) => !s.sessionTrigger && stepMatchesPath(s, location.pathname),
      ) ?? null
    );
  }, [
    onboarding?.effectiveSteps,
    onboarding?.helpStepOverride,
    location.pathname,
  ]);

  // Handle pending reservation intents after login
  usePendingReservationHandler();

  // Move an older push service worker onto the current script URL (once per visit)
  useServiceWorkerRefresh();

  useEffect(() => {
    const cleanup = restoreScrollPositionOnBlur();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  const {
    isDesktopOrTablet,
    additionalLayoutButtonIconType,
    userStudios,
    isAuthenticated,
    selectedStudio,
    currentUser,
    setShowSidebar,
    requestStudioSwitch,
    pendingStudioAccess,
    additionalLayoutButtonOnClick,
    isStudioAccessCheckPending,
  } = useAppContext();

  const { isLoading } = useApiHelperAppContext();
  const supportUnreadCount = useSupportUnreadCount();

  // What a global admin announced to the user's role: asked for once when the app opens,
  // and only after the dialogs below that come first (studio picker, studio access, phone
  // number) are out of the way.
  const announcements = useAnnouncements({
    enabled:
      !showSelectStudioDialog && !!currentUser?.phoneNumber && !pendingStudioAccess,
  });

  useEffect(() => {
    const isInvalidStudio =
      !selectedStudio || !userStudios.some((x) => x.id === selectedStudio.id);

    // Check if URL has a selectedStudio parameter that needs to be processed
    const searchParams = new URLSearchParams(location.search);
    const hasUrlStudioParam = searchParams.has("selectedStudio");

    // Don't show dialog if URL has a studio parameter (let URL sync handle it first),
    // nor over a global admin's studio access dialog (it comes back if they say no).
    // Nor on the review page an e-mail opens: its token already names the studio, and a
    // visitor who never picked one must not be stopped by the picker.
    const isReviewInvitePage = !!matchPath(routePaths.review, location.pathname);
    const shouldShowDialog =
      isInvalidStudio &&
      userStudios.length > 1 &&
      !hasUrlStudioParam &&
      !pendingStudioAccess &&
      !isReviewInvitePage;

    setShowSelectStudioDialog(shouldShowDialog);

    if (isInvalidStudio && userStudios.length === 1 && !pendingStudioAccess) {
      requestStudioSwitch(userStudios[0].id);
    }
  }, [
    selectedStudio,
    isAuthenticated,
    location.pathname,
    location.search,
    userStudios,
    pendingStudioAccess,
  ]);

  const backgroundColorPrimary = generateHexFromString(
    selectedStudio?.backgroundColorPrimary ?? Default_Background_Primary_Color,
  );

  const backgroundColorSecondary = generateHexFromString(
    selectedStudio?.backgroundColorSecondary ??
      Default_Background_Secondary_Color,
  );

  const buttonBackgroundColor = generateHexFromString(
    selectedStudio?.buttonBackgroundColor ?? Default_Button_Background_Color,
  );

  const buttonTextColor = generateHexFromString(
    selectedStudio?.buttonTextColor ?? "ffffff",
  );

  applyThemeColors(
    backgroundColorPrimary,
    backgroundColorSecondary,
    buttonBackgroundColor,
    buttonTextColor,
    // Null for a studio that never chose a Design Studio accent: it keeps today's look.
    getStudioAccent(selectedStudio),
  );

  // Check if current route should render without layout
  const shouldRenderWithoutLayout = routesWithoutLayout.includes(
    location.pathname,
  );

  // A global admin's remembered studio stays closed until the access dialog is answered.
  const pageContent = isStudioAccessCheckPending ? null : children;

  return (
    <div className={shouldRenderWithoutLayout ? undefined : styles.container}>
      <Title>{selectedStudio?.name ?? ""} • Planny</Title>

      {/* Global loader - always rendered regardless of layout */}
      <RenderWhen condition={isLoading}>
        <Loading />
      </RenderWhen>

      {/* Sidebar is always rendered regardless of layout */}
      <SharedSidebar />

      {/* Conditional layout rendering */}
      {shouldRenderWithoutLayout ? (
        // No layout - just render children
        pageContent
      ) : (
        <>
          <div className={classNames(styles.root, className)}>
            <div className={styles.root_header}>
              <div className={styles.environment}>
                {import.meta.env.VITE_ENVIRONMENT}
              </div>
              <div
                className={styles.root_header_button_burger_menu_wrapper}
                data-onboarding="burger-menu"
              >
                <IconButton
                  className={classNames(
                    styles.root_header_button,
                    styles.root_header_button_burger_menu,
                    styles.root_header_button_dimensions,
                  )}
                  iconType={IconType.BurgerMenuIcon}
                  onClick={() => setShowSidebar(true)}
                />
                {supportUnreadCount > 0 && (
                  <span
                    className={styles.root_header_button_burger_menu_unread_dot}
                  />
                )}
                <RenderWhen condition={!!currentPageStep && !!onboarding}>
                  <button
                    className={styles.root_header_help_button}
                    data-onboarding="help-button"
                    onClick={() =>
                      onboarding!.activateStep(currentPageStep!.id)
                    }
                    title="Помощ за тази страница"
                  >
                    ?
                  </button>
                </RenderWhen>
              </div>
              <div className={styles.root_header_company_name}>
                <OverflowingParagraph
                  content={selectedStudio?.name ?? ""}
                  adaptive
                />
              </div>
              <div className={styles.root_header_right_group}>
                <div
                  className={classNames(
                    styles.root_header_button_dimensions,
                    styles.root_header_button_additional_button_wrapper,
                  )}
                  data-onboarding="add-button"
                >
                  <RenderWhen
                    condition={
                      additionalLayoutButtonIconType !== undefined &&
                      additionalLayoutButtonOnClick !== undefined
                    }
                  >
                    <IconButton
                      className={classNames(
                        styles.root_header_button,
                        styles.root_header_button_additional_button,
                        styles.root_header_button_dimensions,
                      )}
                      iconColor="#fff"
                      iconType={additionalLayoutButtonIconType!}
                      onClick={() => additionalLayoutButtonOnClick!()}
                    />
                  </RenderWhen>
                </div>
              </div>
            </div>

            <div className={classNames(styles.root_content)}>{pageContent}</div>
          </div>

          {/* Floating, route-driven back button (mobile/tablet) */}
          <BackButton />
        </>
      )}

      {/* Dialogs are always rendered regardless of layout */}
      <AppDialog
        header="Избиране на обект"
        visible={showSelectStudioDialog}
        style={{ width: isDesktopOrTablet ? "450px" : "90vw" }}
        onHide={() => {}}
        closable={false}
      >
        <SelectStudioForm
          setShowSelectStudioDialog={setShowSelectStudioDialog}
        />
      </AppDialog>

      <StudioAccessConfirmDialog />

      {/* Not in a profile a global admin opened from the admin panel: adding a phone there
          would change the account and give consents in the user's name. */}
      <AppDialog
        header="Телефонен номер"
        visible={
          !showSelectStudioDialog &&
          !!isAuthenticated &&
          !currentUser?.phoneNumber &&
          !currentUser?.impersonatedBy
        }
        style={{ width: isDesktopOrTablet ? "450px" : "90vw" }}
        closable={false}
        onHide={() => {}}
      >
        <AddPhoneNumberForm
          returnUrl={
            location.pathname === routePaths.portalAppointment
              ? location.pathname + location.search
              : undefined
          }
        />
      </AppDialog>

      <AnnouncementDialog
        announcement={announcements.announcement}
        visible={announcements.isVisible}
        position={announcements.position}
        total={announcements.total}
        onConfirm={announcements.onConfirm}
        onLater={announcements.onLater}
      />

      {/* Staff are asked about notifications on opening the platform, after the dialogs above.
          Not mounted while announcements load or are open: the invitation works out whether to
          appear (and waits its own moment) only once they are done, so the two never stack. */}
      <RenderWhen
        condition={
          !showSelectStudioDialog &&
          !!currentUser?.phoneNumber &&
          !announcements.isBlockingOtherDialogs
        }
      >
        <NotificationPermissionDialog
          audience={NotificationDialogAudience.Staff}
        />
      </RenderWhen>

      <ImpersonationBanner />
    </div>
  );
};
